<template>
    <div class="live-processes-page">
        <Page>
            <template #title>
                <h1>{{ pageTitle }}</h1>
            </template>
            <template #default>
                <div v-if="loading" class="loading">
                    <Spinner />
                </div>
                <div v-else-if="error" class="error">
                    <Message severity="error">{{ error }}</Message>
                </div>
                <div v-else class="processes-grid">
                    <Card
                        v-for="process in processes"
                        :key="process.identity"
                        class="process-card"
                        @click="navigateToProcess(process.identity)"
                    >
                        <template #title>{{
                            process.name || process.identity
                        }}</template>
                        <template #content>
                            <div v-if="process.metrics" class="metrics-preview">
                                <div class="metric">
                                    <span class="metric-label"
                                        >Total Events:</span
                                    >
                                    <span class="metric-value">{{
                                        process.metrics.total_events
                                    }}</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label"
                                        >Error Rate:</span
                                    >
                                    <span class="metric-value"
                                        >{{
                                            process.metrics.error_rate.toFixed(
                                                2
                                            )
                                        }}%</span
                                    >
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>
            </template>
        </Page>
    </div>
</template>

<script setup lang="ts">
import Card from "primevue/card";
import Message from "primevue/message";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import Page from "@/components/Page.vue";
import { useStekManager } from "@/composables/stek-manager";
import { useClient, useServices } from "@/lib/openapi";
import type { LiveMetrics } from "@/lib/openapi/gen/types.gen";
import Spinner from "@/shared/Spinner.vue";

defineOptions({
    name: "ProcessesPage",
});

const route = useRoute();
const router = useRouter();
const { stek } = useStekManager();
const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});
const client = useClient({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

const processes = ref<
    Array<{ identity: string; name?: string; metrics?: LiveMetrics }>
>([]);
const loading = ref(false);
const error = ref<string | null>(null);

const stekName = computed(() => {
    return stek.value?.name || route.params.stekName;
});

const productId = computed(() => {
    return route.query.productId as string | undefined;
});

const productName = ref<string | null>(null);

const pageTitle = computed(() => {
    if (productName.value) {
        return `${stekName.value} - Processes in ${productName.value}`;
    }
    return `${stekName.value} - All Processes`;
});

async function fetchProcesses() {
    loading.value = true;
    error.value = null;
    try {
        const allProcesses: Array<Record<string, unknown>> = [];

        if (productId.value) {
            // Fetch processes for a specific product
            const processesResult =
                await api.startProcessProcessMgtGetProcessesByProductStartPost({
                    body: { product_id: productId.value },
                });
            if (processesResult.response.ok && processesResult.data) {
                let processesData: Array<Record<string, unknown>> = [];
                if (Array.isArray(processesResult.data)) {
                    processesData = processesResult.data;
                } else if (
                    processesResult.data &&
                    Array.isArray(processesResult.data.items)
                ) {
                    processesData = processesResult.data.items;
                }
                allProcesses.push(...processesData);
            }

            // Fetch product name for title
            const domainsResult =
                await api.startDomainDomainMgtGetDomainsByStekStartPost();
            if (domainsResult.response.ok && domainsResult.data) {
                let domainsData: Array<Record<string, unknown>> = [];
                if (Array.isArray(domainsResult.data)) {
                    domainsData = domainsResult.data;
                } else if (
                    domainsResult.data &&
                    Array.isArray(domainsResult.data.items)
                ) {
                    domainsData = domainsResult.data.items;
                }

                for (const domain of domainsData) {
                    const productsResult =
                        await api.startProductProductMgtGetProductsByDomainStartPost(
                            {
                                body: {
                                    domain_id:
                                        domain.identity ||
                                        domain.id ||
                                        String(domain),
                                },
                            }
                        );
                    if (productsResult.response.ok && productsResult.data) {
                        let productsData: Array<Record<string, unknown>> = [];
                        if (Array.isArray(productsResult.data)) {
                            productsData = productsResult.data;
                        } else if (
                            productsResult.data &&
                            Array.isArray(productsResult.data.items)
                        ) {
                            productsData = productsResult.data.items;
                        }
                        const product = productsData.find(
                            (p: Record<string, unknown>) =>
                                (p.identity || p.id || String(p)) ===
                                productId.value
                        );
                        if (product) {
                            productName.value =
                                product.name || product.title || null;
                            break;
                        }
                    }
                }
            }
        } else {
            // Fetch all processes across all products
            const domainsResult =
                await api.startDomainDomainMgtGetDomainsByStekStartPost();
            if (domainsResult.response.ok && domainsResult.data) {
                let domainsData: Array<Record<string, unknown>> = [];
                if (Array.isArray(domainsResult.data)) {
                    domainsData = domainsResult.data;
                } else if (
                    domainsResult.data &&
                    Array.isArray(domainsResult.data.items)
                ) {
                    domainsData = domainsResult.data.items;
                }

                for (const domain of domainsData) {
                    const productsResult =
                        await api.startProductProductMgtGetProductsByDomainStartPost(
                            {
                                body: {
                                    domain_id:
                                        domain.identity ||
                                        domain.id ||
                                        String(domain),
                                },
                            }
                        );
                    if (productsResult.response.ok && productsResult.data) {
                        let productsData: Array<Record<string, unknown>> = [];
                        if (Array.isArray(productsResult.data)) {
                            productsData = productsResult.data;
                        } else if (
                            productsResult.data &&
                            Array.isArray(productsResult.data.items)
                        ) {
                            productsData = productsResult.data.items;
                        }

                        for (const product of productsData) {
                            const processesResult =
                                await api.startProcessProcessMgtGetProcessesByProductStartPost(
                                    {
                                        body: {
                                            product_id:
                                                product.identity ||
                                                product.id ||
                                                String(product),
                                        },
                                    }
                                );
                            if (
                                processesResult.response.ok &&
                                processesResult.data
                            ) {
                                let processesData: Array<
                                    Record<string, unknown>
                                > = [];
                                if (Array.isArray(processesResult.data)) {
                                    processesData = processesResult.data;
                                } else if (
                                    processesResult.data &&
                                    Array.isArray(processesResult.data.items)
                                ) {
                                    processesData = processesResult.data.items;
                                }
                                allProcesses.push(...processesData);
                            }
                        }
                    }
                }
            }
        }

        // Fetch metrics for each process
        const processesWithMetrics = await Promise.all(
            allProcesses.map(async (p: Record<string, unknown>) => {
                try {
                    const response = await client.post<LiveMetrics>({
                        url: "/stek/live_mgt/get_metrics/start",
                        body: {
                            process_id: p.identity || p.id || String(p),
                            hours: 24,
                        },
                    });

                    if (!response.response.ok || !response.data) {
                        return {
                            identity: p.identity || p.id || String(p),
                            name: p.name || p.title,
                        };
                    }

                    const metrics = response.data;
                    return {
                        identity: p.identity || p.id || String(p),
                        name: p.name || p.title,
                        metrics,
                    };
                } catch {
                    return {
                        identity: p.identity || p.id || String(p),
                        name: p.name || p.title,
                    };
                }
            })
        );
        processes.value = processesWithMetrics;
    } catch (e) {
        error.value =
            e instanceof Error ? e.message : "Failed to load processes";
    } finally {
        loading.value = false;
    }
}

function navigateToProcess(processId: string) {
    const orgName = route.params.orgName;
    const stekName = route.params.stekName;
    router.push({
        name: "liveProcessPage",
        params: { orgName, stekName, processId },
    });
}

watch(
    [productId],
    () => {
        fetchProcesses();
    },
    { immediate: true }
);

onMounted(() => {
    fetchProcesses();
});
</script>

<style scoped lang="scss">
.live-processes-page {
    padding: 1rem;
}

.processes-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
}

.process-card {
    cursor: pointer;
    transition:
        transform 0.2s,
        box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
}

.metrics-preview {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.metric {
    display: flex;
    justify-content: space-between;
}

.metric-label {
    font-weight: 500;
}

.metric-value {
    font-weight: bold;
}

.loading,
.error {
    text-align: center;
    padding: 2rem;
}
</style>
